import { AnimationSystem, CollisionSystem, HandlePlacementSystem, HandlePlacementValidation, HistoryApi, InvalidFramePlacementSystem, ModelLoadedEventSystem, PlannerCoreApi, SelectionEventSystem, OutlineSelectedFrameSystem, MeasurementsWidgetSystem, HighlightAddedEntitySystem, UnavailableProductsEventSystem, ConflictOutlineSystem, ConflictSystem, LegsFeature, SuspensionRailSystem, ProductType, TopPanelSystem, DraggableWhenSelectedSystem, RedOutlineSystem, EntityColorSystem, RotationValidation, WireBasketHeightValidation, HistoryEventSystem, HangingFramePlacementValidation } from '@inter-ikea-storageone/range';
import { AutomaticGroupBuilderSystem, AlignmentConnectionCreationSystem, FunctionalMarginSystem, FunctionalMarginValidation, OutlineSystem, PlacementZoneValidation, ProppingSystem, StandardSelectionSystem, RequiredConnectorsSatisfiedValidation, HostConnectorRotationValidation } from '@inter-ikea-core/core';
import { SmastadCameraApi } from './SmastadCameraApi';
import { SmastadEntityApi } from './SmastadEntityApi';
import { SmastadProductApi } from './SmastadProductApi';
import { SmastadSceneApi } from './SmastadSceneApi';
import { SmastadConflictResolver } from './resolvers/smastadConflictResolver';
import { InteriorPlacementValidationSystem } from './systems/InteriorPlacementValidationSystem';
import { SimpleOutlineSelectedSystem } from '@inter-ikea-core/core-toolbox';
import { DynamicProductCatalog } from '@inter-ikea-core/core';
import { FrontMeasurementsSystem } from './systems/FrontMeasurementsSystem';
export const defaultAgeGroup = '3-7';
export const ageGroups = ['0-2', '3-7', '8-14'];
export class SmastadCoreApi extends PlannerCoreApi {
    bootstrap(config) {
        if (!this.core) {
            throw new Error('Core not initialized yet');
        }
        // Setup core base functionality
        const { selectionService, placementService } = super.initializeBaseCoreFunctionality();
        // Register core systems
        this.registerSystem(new OutlineSystem());
        this.registerSystem(new AutomaticGroupBuilderSystem(this.core.services.entityManager));
        this.registerSystem(new SimpleOutlineSelectedSystem());
        this.registerSystem(new StandardSelectionSystem(this.core.services.entityManager, this.core.services.sceneManager));
        this.registerSystem(new FunctionalMarginSystem());
        this.registerSystem(new AlignmentConnectionCreationSystem(this.getServices()));
        // Register core validations
        this.registerValidation(new HostConnectorRotationValidation());
        this.registerValidation(new RequiredConnectorsSatisfiedValidation());
        this.registerValidation(new FunctionalMarginValidation(false, this.core.services.entityManager));
        // Give a warning if an entity is placed outside its placement zone
        this.registerValidation(new PlacementZoneValidation());
        const collisionValidation = this.core
            .getValidations()
            .find((validation) => validation.id === 'Collision');
        collisionValidation.enableExhaustiveValidation();
        // Setup planner APIs
        this.history = new HistoryApi(this);
        this.products = new SmastadProductApi(this.core.services, placementService, this.reportErrorToSentry);
        this.entities = new SmastadEntityApi(selectionService, this.core, placementService, this.ipexViewer, this.reportErrorToSentry);
        this.conflictResolver = new SmastadConflictResolver(this);
        // Register planner validations
        this.registerValidation(new HangingFramePlacementValidation(this));
        this.registerValidation(new RotationValidation(this));
        this.registerValidation(new WireBasketHeightValidation(this, ProductType.SMASTAD_WIRE_BASKET_ASM));
        this.registerValidation(new HandlePlacementValidation(this));
        // Register planner systems
        this.registerSystem(new HistoryEventSystem(this));
        this.registerSystem(new ConflictSystem(this));
        this.registerSystem(new ConflictOutlineSystem(this));
        this.registerSystem(new UnavailableProductsEventSystem(this));
        this.registerSystem(new ModelLoadedEventSystem(this));
        this.registerSystem(new InvalidFramePlacementSystem(this));
        this.registerSystem(new CollisionSystem(this, []));
        this.registerSystem(new SelectionEventSystem(this));
        this.registerSystem(new AnimationSystem(this));
        this.registerSystem(new HandlePlacementSystem(this));
        this.registerSystem(new SuspensionRailSystem(this, undefined, ProductType.SMASTAD_SUSPENSION_RAIL, 3000));
        this.registerSystem(new TopPanelSystem(this, ProductType.SMASTAD_TOP));
        this.registerSystem(new DraggableWhenSelectedSystem(this, [
            ProductType.SMASTAD_HINGE_DOOR_ASM,
            ProductType.SMASTAD_DRAWER_ASM,
            ProductType.SMASTAD_BENCH_DRAWER
        ]));
        this.registerSystem(new OutlineSelectedFrameSystem(this));
        this.registerSystem(new MeasurementsWidgetSystem(this));
        this.registerSystem(new HighlightAddedEntitySystem(this));
        this.registerSystem(new InteriorPlacementValidationSystem(this));
        this.registerSystem(new RedOutlineSystem(this));
        this.registerSystem(new EntityColorSystem(this));
        this.registerSystem(new FrontMeasurementsSystem(this));
        new LegsFeature(this, this.products.getDefaultLeg());
        return this.loadProductCatalogs(config.productCatalogs);
    }
    initSceneApi() {
        this.scene = new SmastadSceneApi(this.core, this.ipexViewer, this.reportErrorToSentry);
    }
    initCameraApi(canvas) {
        this.camera = new SmastadCameraApi(this.core.services.sceneManager, this.ipexViewer, this.core.services.entityManager, this, canvas);
    }
    getMainProductCatalog() {
        return this.catalogs['smastad'];
    }
    /**
     *
     * @override
     */
    async loadScene(config, setRoom) {
        // We need to wait for the scene to load before loading the legs system
        // otherwise the frames will not get the correct components added.
        this.getSystem('LegsSystem')?.disable();
        this.validations['LegsValidation']?.disable();
        this.validations['HangingFramePlacementValidation']?.disable();
        await super.loadScene(config, setRoom);
        // When the scene has been loaded we can enable the legs system again
        this.validations['LegsValidation']?.enable();
        this.getSystem('LegsSystem')?.enable();
        this.validations['HangingFramePlacementValidation']?.enable();
    }
    /**
     * @override
     */
    async loadProppingCatalog(productCatalog, ageGroup) {
        if (this.catalogs['propping']) {
            this.core.services.productManager.removeCatalog(this.catalogs['propping']);
        }
        const ageGroupFilteredProppingCatalogProducts = productCatalog.catalog.products.filter(product => product.meta?.ageGroup === ageGroup || !product.meta?.ageGroup);
        const ageGroupFilteredProppingCatalog = {
            ...productCatalog.catalog,
            products: ageGroupFilteredProppingCatalogProducts
        };
        this.catalogs['propping'] = new DynamicProductCatalog(JSON.stringify(ageGroupFilteredProppingCatalog), JSON.stringify(productCatalog.template), productCatalog.assetsPath);
        this.core.services.productManager.addCatalog(this.catalogs['propping']);
        const existingProppingSystem = this.getSystem('ProppingSystem');
        if (existingProppingSystem) {
            existingProppingSystem.dispose();
            this.core.services.entityManager.unregisterSystem(existingProppingSystem);
        }
        this.core.services.entityManager.registerSystem(new ProppingSystem(this.catalogs['propping'], this.core.services));
    }
}
//# sourceMappingURL=SmastadCoreApi.js.map